import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Trip } from '../types/Trip';
import useMap from '../hooks/useMap';
import { useTrip } from '../contexts/TripContext';

interface TripMapProps {
   trip: Trip;
   height?: string | number;
}

const TripMap: React.FC<TripMapProps> = ({ trip, height = 800 }) => {
   const [error, setError] = useState<string | null>(null);
   const [photos, setPhotos] = useState<any[]>([]);
   const { getTripPhotos } = useTrip();
   const {
      mapLoaded: loading,
      map,
      mapContainer,
      resetMap,
      extendBounds,
      clearMarkers,
      createMapMarker,
   } = useMap();

   const truncateDescription = (text: string | undefined, maxWords: number = 100): string => {
      if (!text) return '';
      const words = text.split(' ');
      if (words.length <= maxWords) return text;
      return words.slice(0, maxWords).join(' ') + '...';
   };

   const addAccommodationMarkers = () => {
      try {
         if (!map) {
            return;
         }

         // Group accommodations by coordinates
         const accommodationsByLocation = trip.accommodations?.reduce(
            (acc: { [key: string]: any[] }, accommodation: any) => {
               if (!accommodation.coordinates) return acc;
               const key = `${accommodation.coordinates[0]},${accommodation.coordinates[1]}`;
               if (!acc[key]) acc[key] = [];
               acc[key].push(accommodation);
               return acc;
            },
            {}
         );

         if (accommodationsByLocation) {
            Object.entries(accommodationsByLocation).forEach(
               ([locationKey, locationAccommodations]) => {
                  const firstAccommodation = locationAccommodations[0];
                  const coordinates = firstAccommodation.coordinates;

                  const color = '#4caf50';

                  const el = document.createElement('div');
                  el.className = 'accommodation-marker';
                  el.style.width = '50px';
                  el.style.height = '50px';
                  el.style.position = 'relative';

                  // Create the main marker circle
                  const markerCircle = document.createElement('div');
                  markerCircle.style.width = '100%';
                  markerCircle.style.height = '100%';
                  markerCircle.style.borderRadius = '50%';
                  markerCircle.style.border = `4px solid ${color}`;
                  markerCircle.style.boxShadow = '0 0 15px rgba(0,0,0,0.6)';
                  markerCircle.style.backgroundColor = 'white';
                  markerCircle.style.display = 'flex';
                  markerCircle.style.justifyContent = 'center';
                  markerCircle.style.alignItems = 'center';
                  markerCircle.style.zIndex = '10';

                  // Add appropriate icon based on accommodation type
                  let iconSvg = `<svg viewBox="0 0 24 24" width="30" height="30" fill="${color}">
                  <path d="M19,7H11V14H3V5H1V20H3V17H21V20H23V11A4,4 0 0,0 19,7M7,13A3,3 0 0,0 10,10A3,3 0 0,0 7,7A3,3 0 0,0 4,10A3,3 0 0,0 7,13Z" />
               </svg>`;

                  markerCircle.innerHTML = iconSvg;
                  el.appendChild(markerCircle);

                  // Add count badge if multiple accommodations
                  if (locationAccommodations.length > 1) {
                     const countBadge = document.createElement('div');
                     countBadge.style.position = 'absolute';
                     countBadge.style.top = '-5px';
                     countBadge.style.right = '-5px';
                     countBadge.style.backgroundColor = color;
                     countBadge.style.color = 'white';
                     countBadge.style.borderRadius = '50%';
                     countBadge.style.width = '20px';
                     countBadge.style.height = '20px';
                     countBadge.style.display = 'flex';
                     countBadge.style.alignItems = 'center';
                     countBadge.style.justifyContent = 'center';
                     countBadge.style.fontSize = '12px';
                     countBadge.style.fontWeight = 'bold';
                     countBadge.textContent = locationAccommodations.length.toString();
                     el.appendChild(countBadge);
                  }

                  const label = document.createElement('div');
                  label.style.position = 'absolute';
                  label.style.top = '-10px';
                  label.style.left = '-50%';
                  label.style.backgroundColor = 'white';
                  label.style.color = 'green';
                  label.style.padding = '4px 10px';
                  label.style.borderRadius = '12px';
                  label.style.fontSize = '14px';
                  label.style.fontWeight = 'bold';
                  label.style.whiteSpace = 'nowrap';
                  label.style.zIndex = '1000';
                  label.innerHTML = `${locationAccommodations.length} ${locationAccommodations.length > 1 ? 'Stays' : 'Stay'}`;
                  el.appendChild(label);

                  // Create grid layout for accommodations in popup
                  const popupContent = `
                  <div style="max-width: 280px; padding: 8px;">
                     <div style="
                        display: grid;
                        grid-template-columns: repeat(${Math.min(2, locationAccommodations.length)}, 1fr);
                        gap: 8px;
                        margin-bottom: 8px;
                     ">
                        ${locationAccommodations
                           .map(
                              (accommodation: any) => `
                           <div style="position: relative; background: white; border-radius: 8px; padding: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                              <h3 style="margin: 0 0 8px 0; color: ${color}; font-size: 14px;">${accommodation.name}</h3>
                              <p style="margin: 0 0 8px 0; font-size: 12px;">${accommodation.address}</p>
                              ${
                                 accommodation.checkIn
                                    ? `
                                 <div style="font-size: 11px; color: #666;">
                                    ${new Date(accommodation.checkIn).toLocaleDateString()} - 
                                    ${new Date(accommodation.checkOut).toLocaleDateString()}
                                 </div>
                              `
                                    : ''
                              }
                           </div>
                        `
                           )
                           .join('')}
                     </div>
                     ${
                        locationAccommodations.length > 2
                           ? `
                        <div style="text-align: center; margin-top: 8px;">
                           <span style="color: #666; font-size: 12px;">
                              +${locationAccommodations.length - 2} more stay${locationAccommodations.length - 2 > 1 ? 's' : ''}
                           </span>
                        </div>
                     `
                           : ''
                     }
                  </div>
               `;

                  const marker = createMapMarker({
                     element: el,
                  });

                  marker
                     .setLngLat([coordinates[0], coordinates[1]])
                     .setPopup(new mapboxgl.Popup({ offset: 30 }).setHTML(popupContent))
                     .addTo(map!);

                  extendBounds([marker.getLngLat().lng, marker.getLngLat().lat]);
               }
            );
         }
      } catch (error) {
         console.error('Error adding accommodation markers:', error);
         setError('Failed to add accommodation markers');
      }
   };

   const tripMarker = () => {
      try {
         if (!map) {
            console.log('Map not available for trip markers');
            return;
         }

         // Group trip locations by coordinates
         const tripLocations = [];
         if (trip?.primaryDestination?.coordinates) {
            tripLocations.push(trip.primaryDestination);
         }
         if (trip?.locations && Array.isArray(trip.locations)) {
            tripLocations.push(...trip.locations.filter(loc => loc.coordinates));
         }

         if (tripLocations.length === 0) {
            console.log('No valid trip locations with coordinates found');
            return;
         }

         const locationsByCoordinates = tripLocations.reduce(
            (acc: { [key: string]: any[] }, location: any) => {
               if (!location.coordinates || !Array.isArray(location.coordinates) || location.coordinates.length < 2) {
                  console.warn('Invalid coordinates found:', location);
                  return acc;
               }
               const [lng, lat] = location.coordinates;
               if (isNaN(lng) || isNaN(lat) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
                  console.warn('Invalid coordinate values:', { lng, lat });
                  return acc;
               }
               const key = `${lng},${lat}`;
               if (!acc[key]) acc[key] = [];
               acc[key].push(location);
               return acc;
            },
            {}
         );

         Object.entries(locationsByCoordinates).forEach(([locationKey, locations]) => {
            const firstLocation = locations[0];
            const coordinates = firstLocation.coordinates;

            if (!coordinates || !Array.isArray(coordinates) || coordinates.length < 2) {
               console.warn('Skipping marker creation due to invalid coordinates:', firstLocation);
               return;
            }

            const el = document.createElement('div');
            el.className = 'primary-destination-marker';
            el.style.width = '40px';
            el.style.height = '40px';
            el.style.position = 'relative';
            el.style.borderRadius = '50%';
            el.style.border = '3px solid #e91e63';
            el.style.boxShadow = '0 0 10px rgba(0,0,0,0.5)';
            el.style.backgroundColor = 'white';
            el.style.display = 'flex';
            el.style.justifyContent = 'center';
            el.style.alignItems = 'center';

            // Location icon
            el.innerHTML = `
               <svg viewBox="0 0 24 24" width="24" height="24" fill="#e91e63">
                  <path d="M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z" />
               </svg>
            `;

            // Add count badge if multiple locations
            if (locations.length > 1) {
               const countBadge = document.createElement('div');
               countBadge.style.position = 'absolute';
               countBadge.style.top = '-5px';
               countBadge.style.right = '-5px';
               countBadge.style.backgroundColor = '#e91e63';
               countBadge.style.color = 'white';
               countBadge.style.borderRadius = '50%';
               countBadge.style.width = '18px';
               countBadge.style.height = '18px';
               countBadge.style.display = 'flex';
               countBadge.style.alignItems = 'center';
               countBadge.style.justifyContent = 'center';
               countBadge.style.fontSize = '10px';
               countBadge.style.fontWeight = 'bold';
               countBadge.textContent = locations.length.toString();
               el.appendChild(countBadge);
            }

            // Add label
            const label = document.createElement('div');
            label.style.position = 'absolute';
            label.style.bottom = '-30px';
            label.style.left = '50%';
            label.style.transform = 'translateX(-50%)';
            label.style.backgroundColor = 'white';
            label.style.color = '#e91e63';
            label.style.padding = '4px 8px';
            label.style.borderRadius = '10px';
            label.style.fontSize = '13px';
            label.style.fontWeight = 'bold';
            label.style.whiteSpace = 'nowrap';
            label.style.zIndex = '1000';
            label.innerHTML = `${locations.length} ${locations.length > 1 ? 'Trips' : 'Trip'}`;
            el.appendChild(label);

            const marker = createMapMarker({
               element: el,
            });

            // Create grid layout for locations in popup
            const popupContent = `
               <div style="max-width: 280px; padding: 8px;">
                  <div style="
                     display: grid;
                     grid-template-columns: repeat(${Math.min(2, locations.length)}, 1fr);
                     gap: 8px;
                     margin-bottom: 8px;
                  ">
                     ${locations
                        .map(
                           (location: any) => `
                        <div style="position: relative; ">
                           ${
                              trip?.coverPhotoUrl
                                 ? `
                              <img 
                                 src="${trip.coverPhotoUrl}" 
                                 alt="${trip.title}" 
                                 style="width: 100%; height: 120px; object-fit: cover; border-radius: 8px;"
                              />
                           `
                                 : `
                              <div style="
                                 width: 100%; 
                                 height: 120px; 
                                 background: #e91e63; 
                                 border-radius: 8px;
                                 display: flex;
                                 align-items: center;
                                 justify-content: center;
                              ">
                                 <svg viewBox="0 0 24 24" width="40" height="40" fill="white">
                                    <path d="M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z" />
                                 </svg>
                              </div>
                           `
                           }
                           <div style="
                              position: absolute;
                              bottom: 0;
                              left: 0;
                              right: 0;
                              background: rgba(0,0,0,0.7);
                              color: white;
                              padding: 4px 8px;
                              font-size: 12px;
                              border-radius: 0 0 8px 8px;
                           ">${location.name}</div>
                           ${
                              location.description
                                 ? `
                              <div style="
                                 position: absolute;
                                 top: 0;
                                 left: 0;
                                 right: 0;
                                 background: rgba(0,0,0,0.7);
                                 color: white;
                                 padding: 4px 8px;
                                 font-size: 10px;
                                 border-radius: 8px 8px 0 0;
                              ">${truncateDescription(location.description, 20)}</div>
                           `
                                 : ''
                           }
                           ${
                              trip?.title
                                 ? `
                              <div style="
                                 position: absolute;
                                 top: 50%;
                                 left: 50%;
                                 transform: translate(-50%, -50%);
                                 background: rgba(0,0,0,0.7);
                                 color: white;
                                 padding: 4px 8px;
                                 font-size: 10px;
                                 border-radius: 4px;
                                 text-align: center;
                              ">${trip.title}</div>
                           `
                                 : ''
                           }
                        </div>
                     `
                        )
                        .join('')}
                  </div>
                  ${
                     locations.length > 2
                        ? `
                     <div style="text-align: center; margin-top: 8px;">
                        <span style="color: #666; font-size: 12px;">
                           +${locations.length - 2} more location${locations.length - 2 > 1 ? 's' : ''}
                        </span>
                     </div>
                  `
                        : ''
                  }
               </div>
            `;

            marker
               .setLngLat([coordinates[0], coordinates[1]])
               .setPopup(new mapboxgl.Popup({ offset: 25 }).setHTML(popupContent))
               .addTo(map!);

            extendBounds([marker.getLngLat().lng, marker.getLngLat().lat]);
         });
      } catch (error) {
         console.error('Error adding trip marker:', error);
         setError('Failed to add trip marker');
      }
   };

   const addPhotoMarkers = async () => {
      try {
         if (!map) return;

         const tripPhotos = await getTripPhotos(trip.id || '');
         setPhotos(tripPhotos);

         // Group photos by coordinates
         const photosByLocation = tripPhotos.reduce((acc: { [key: string]: any[] }, photo: any) => {
            if (!photo.location) return acc;
            const key = `${photo.location.coordinates.latitude},${photo.location.coordinates.longitude}`;
            if (!acc[key]) acc[key] = [];
            acc[key].push(photo);
            return acc;
         }, {});

         // Create markers for each location
         Object.entries(photosByLocation).forEach(([locationKey, locationPhotos]) => {
            const firstPhoto = locationPhotos[0];
            const coordinates = firstPhoto.location.coordinates;

            const el = document.createElement('div');
            el.className = 'photo-marker';
            el.style.width = '40px';
            el.style.height = '40px';
            el.style.position = 'relative';
            el.style.borderRadius = '50%';
            el.style.border = '3px solid #2196f3';
            el.style.boxShadow = '0 0 10px rgba(0,0,0,0.5)';
            el.style.backgroundColor = 'white';
            el.style.display = 'flex';
            el.style.justifyContent = 'center';
            el.style.alignItems = 'center';

            // Camera icon with count badge
            el.innerHTML = `
               <div style="position: relative;">
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="#2196f3">
                     <path d="M4,4H7L9,2H15L17,4H20A2,2 0 0,1 22,6V18A2,2 0 0,1 20,20H4A2,2 0 0,1 2,18V6A2,2 0 0,1 4,4M12,7A5,5 0 0,0 7,12A5,5 0 0,0 12,17A5,5 0 0,0 17,12A5,5 0 0,0 12,7M12,9A3,3 0 0,1 15,12A3,3 0 0,1 12,15A3,3 0 0,1 9,12A3,3 0 0,1 12,9Z" />
                  </svg>
                  ${
                     locationPhotos.length > 1
                        ? `
                     <div style="
                        position: absolute;
                        top: -5px;
                        right: -5px;
                        background: #2196f3;
                        color: white;
                        border-radius: 50%;
                        width: 18px;
                        height: 18px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 10px;
                        font-weight: bold;
                     ">${locationPhotos.length}</div>
                  `
                        : ''
                  }
               </div>
            `;

            // Add label
            const label = document.createElement('div');
            label.style.position = 'absolute';
            label.style.bottom = '-25px';
            label.style.left = '50%';
            label.style.transform = 'translateX(-50%)';
            label.style.backgroundColor = 'white';
            label.style.color = '#2196f3';
            label.style.padding = '2px 8px';
            label.style.borderRadius = '10px';
            label.style.fontSize = '12px';
            label.style.fontWeight = 'bold';
            label.style.whiteSpace = 'nowrap';
            label.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
            label.textContent = `${locationPhotos.length} Photo${locationPhotos.length > 1 ? 's' : ''}`;
            el.appendChild(label);

            const marker = createMapMarker({
               element: el,
            });

            // Create grid layout for photos in popup
            const popupContent = `
               <div style="max-width: 280px; padding: 8px;">
                  <div style="
                     display: grid;
                     grid-template-columns: repeat(${Math.min(2, locationPhotos.length)}, 1fr);
                     gap: 8px;
                     margin-bottom: 8px;
                  ">
                     ${locationPhotos
                        .map(
                           (photo: any) => `
                        <div style="position: relative;">
                           <img 
                              src="${photo.url}" 
                              alt="${photo.title || 'Photo'}" 
                              style="width: 100%; height: 120px; object-fit: cover; border-radius: 8px;"
                           />
                           ${
                              photo.title
                                 ? `
                              <div style="
                                 position: absolute;
                                 bottom: 0;
                                 left: 0;
                                 right: 0;
                                 background: rgba(0,0,0,0.7);
                                 color: white;
                                 padding: 4px 8px;
                                 font-size: 12px;
                                 border-radius: 0 0 8px 8px;
                              ">${photo.title}</div>
                           `
                                 : ''
                           }
                           ${
                              photo.location?.name
                                 ? `
                              <div style="
                                 position: absolute;
                                 top: 0;
                                 left: 0;
                                 right: 0;
                                 background: rgba(0,0,0,0.7);
                                 color: white;
                                 padding: 4px 8px;
                                 font-size: 10px;
                                 border-radius: 8px 8px 0 0;
                              ">${photo.location?.name}</div>
                           `
                                 : ''
                           }
                        </div>
                     `
                        )
                        .join('')}
                  </div>
                  ${
                     locationPhotos.length > 2
                        ? `
                     <div style="text-align: center; margin-top: 8px;">
                        <span style="color: #666; font-size: 12px;">
                           +${locationPhotos.length - 2} more photo${locationPhotos.length - 2 > 1 ? 's' : ''}
                        </span>
                     </div>
                  `
                        : ''
                  }
               </div>
            `;

            marker
               .setLngLat([coordinates.longitude, coordinates.latitude])
               .setPopup(new mapboxgl.Popup({ offset: 25 }).setHTML(popupContent))
               .addTo(map!);

            extendBounds([marker.getLngLat().lng, marker.getLngLat().lat]);
         });
      } catch (error) {
         console.error('Error adding photo markers:', error);
         setError('Failed to add photo markers');
      }
   };

   const loadMarkers = () => {
      clearMarkers();
      tripMarker();
      addAccommodationMarkers();
      addPhotoMarkers();
   };

   useEffect(() => {
      loadMarkers();
   }, [JSON.stringify({ ...trip }), map, mapContainer.current]);

   useEffect(() => {
      loadMarkers();
   }, []);

   return (
      <Box sx={{ position: 'relative', maxHeight: 600, height: 400, width: '100%' }}>
         {loading && (
            <Box
               sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1,
                  backgroundColor: 'rgba(255,255,255,0.5)',
               }}
            >
               <CircularProgress />
            </Box>
         )}

         {error && (
            <Box
               sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1,
                  backgroundColor: 'rgba(255,255,255,0.8)',
                  padding: 2,
               }}
            >
               <Typography color='error' align='center'>
                  {error}
               </Typography>
            </Box>
         )}

         <Box
            ref={mapContainer}
            sx={{
               height: '100%',
               width: '100%',
               //  borderRadius: 1,
               //  overflow: 'hidden',
            }}
         />
      </Box>
   );
};

export default TripMap; 